import React from 'react';

import Paper from '@material-ui/core/Paper';
import { navigate } from '@reach/router';

import { IEventItem } from '../../../../store/player/events/store';
import { IUserStore } from '../../../../store/user/store';

import Button from '../../../../components/Button';
import CardHeader from '../../../../components/CardHeader';
import CreditCard from '../../../../components/CreditCard';
import CreditCardList from '../../../../components/CreditCardList/CreditCardList';
import { Card, FancyCards } from '../../../../components/FancyCards/FancyCards';

import { EventCheckout } from '../EventCheckout/EventCheckout';
import EventConfirm from '../EventConfirm/EventConfirm';
import EventLogin from '../EventLogin';
import EventMessage from '../EventMessage';
import { EventMessageSent } from '../EventMessageSent/EventMessageSent';
import EventRegistry from '../EventRegistry';
import EventSuccess from '../EventSuccess';
import EventWithdraw from '../EventWithdraw';
import { EventWithdrawConfirmation } from '../EventWithdrawConfirmation/EventWithdrawConfirmation';

import { EventHomeHeights } from '../Utils/EventHomeHeights';
import { EventHomeViews } from '../Utils/EventHomeViews';
import { getCheckoutHeight } from '../Utils/getCheckoutHeight';
import { getConfirmHeight } from '../Utils/getConfirmHeight';
import { getRegistryHeight } from '../Utils/getRegistryHeight';

interface IEventHomeCards {
  cardElement: any;
  checkout: any;
  creditCardFormReady: any;
  event: IEventItem;
  eventId: any;
  getRegistrationSummary: any;
  isAuthenticated: boolean;
  noteMessage: any;
  onCardComplete: any;
  onCardDone: any;
  onCardSubmit: any;
  onMessageChange: any;
  onPaymentSuccess: any;
  onSelectPayment: any;
  onWithdraw: any;
  saveCheckout: any;
  selectCard: any;
  selectedCard: any;
  sendMessage: any;
  user: IUserStore;
}

export const EventHomeCards = (props: IEventHomeCards) => {
  const {
    cardElement,
    checkout,
    creditCardFormReady,
    event,
    eventId,
    getRegistrationSummary,
    isAuthenticated,
    noteMessage,
    onCardComplete,
    onCardDone,
    onCardSubmit,
    onMessageChange,
    onPaymentSuccess,
    onSelectPayment,
    onWithdraw,
    saveCheckout,
    selectCard,
    selectedCard,
    sendMessage,
    user: { customer }
  } = props;

  const sources = customer ? customer.sources : [];
  const { processPayment } = event;

  const remainingSpots =
    event &&
    event.totalPlayers - event.enrollments.filter((e: any) => e.status === 'active').length;

  const clubId = event ? event.club.id : 0;

  return (
    <FancyCards selected={selectedCard}>
      <Card name={`event-stats-${event.name}`} height={getRegistryHeight(event)}>
        <EventRegistry
          item={event}
          onRegister={selectCard(
            isAuthenticated
              ? processPayment
                ? EventHomeViews.CHECKOUT
                : EventHomeViews.CONFIRM
              : EventHomeViews.LOGIN
          )}
          onAddNote={selectCard(EventHomeViews.MESSAGE)}
          onWithdraw={selectCard(isAuthenticated ? EventHomeViews.WITHDRAW : EventHomeViews.LOGIN)}
        />
      </Card>

      <Card name={`event-login-${event.name}`} height={EventHomeHeights.EVENTLOGIN}>
        <EventLogin eventId={eventId} />
      </Card>

      <Card name={`event-checkout-${event.name}`} height={getCheckoutHeight(event)}>
        <EventCheckout
          item={event}
          onContinue={saveCheckout}
          onCancel={selectCard(EventHomeViews.STATS)}
          onCouponCheck={getRegistrationSummary}
        />
      </Card>

      <Card
        name={`credit-card-list-${event.name}`}
        height={
          EventHomeHeights.BILLINGBASE +
          sources.length * EventHomeHeights.CREDITCARD +
          (remainingSpots <= 0 ? EventHomeHeights.DISCLAIMER : 0)
        }
      >
        <div className="event-checkout wrapper">
          <Paper elevation={1} className="paper">
            {remainingSpots <= 0 && (
              <div className="disclaimer-view-home">
                To reserve your spot on the waiting click below. Your card will only be charged if
                you are confirmed as a player in the event.
              </div>
            )}
            <CreditCardList
              iconType="select"
              onAdd={selectCard(EventHomeViews.ADD_PAYMENT)}
              onCancel={selectCard(EventHomeViews.CHECKOUT)}
              onSelect={onSelectPayment}
              sources={sources}
            />
          </Paper>
        </div>
      </Card>

      <Card
        name={`credit-card-${event.name}`}
        height={
          EventHomeHeights.ADDPAYMENT + (remainingSpots <= 0 ? EventHomeHeights.DISCLAIMER : 0)
        }
      >
        <div className="event-checkout add-payment wrapper">
          <Paper elevation={1} className="paper">
            {remainingSpots <= 0 && (
              <div className="disclaimer-view-home">
                YOUR CARD WILL ONLY BE CHARGED IF YOU ARE CONFIRMED AS A PLAYER IN THE EVENT.
              </div>
            )}
            <CardHeader
              label="ADD A NEW PAYMENT METHOD"
              backTo={selectCard(EventHomeViews.SELECT_PAYMENT)}
            />
            <CreditCard onComplete={onCardComplete} onSubmit={onCardSubmit} ref={cardElement} />
            <Button
              variant="contained"
              color="primary"
              onClick={onCardDone}
              disabled={!creditCardFormReady}
              className="button button-shadow"
            >
              ADD CREDIT CARD
            </Button>
          </Paper>
        </div>
      </Card>

      <Card name={`event-confirm-${event.name}`} height={getConfirmHeight(event, checkout)}>
        <EventConfirm
          item={event}
          checkoutState={checkout}
          onContinue={onPaymentSuccess}
          onCancel={selectCard(EventHomeViews.STATS)}
          onPrevious={selectCard(EventHomeViews.SELECT_PAYMENT)}
          onCouponCheck={getRegistrationSummary}
        />
      </Card>

      <Card name={`event-success-${event.name}`}>
        <EventMessageSent item={event} onCancel={() => navigate(`/event/${eventId}`)} />
      </Card>

      <Card name={`event-success-${event.name}`} height={EventHomeHeights.EVENTMESSAGE}>
        <EventMessage
          onMessageChange={onMessageChange}
          message={noteMessage}
          onContinue={sendMessage}
          onSkip={selectCard(EventHomeViews.SUCCESS)}
        />
      </Card>

      <Card name={`event-success-${event.name}`} height={EventHomeHeights.EVENTSUCCESS}>
        <EventSuccess
          item={event}
          onContinue={selectCard(EventHomeViews.MESSAGE)}
          onCancel={() => navigate(`/club/${clubId}`)}
        />
      </Card>

      <Card name={`event-withdraw-${event.name}`} height={EventHomeHeights.EVENTWITHDRAW}>
        <EventWithdraw onCancel={selectCard(EventHomeViews.STATS)} onContinue={onWithdraw} />
      </Card>
      <Card name={`event-withdraw-${event.name}`} height={EventHomeHeights.EVENTWITHDRAW}>
        <EventWithdrawConfirmation
          onCancel={() => navigate(`/club/${clubId}`)}
          onContinue={() => navigate(`/event/${eventId}`)}
        />
      </Card>
    </FancyCards>
  );
};
